import { useCheckout } from '../../hooks/use-checkout.js'
import { ErrorAlert } from '#components/ui/error-alert.jsx'
import { Link } from 'arcway/lib/client'
import { ArrowRight } from 'lucide-react'

import { formatCatalogMoney } from '#components/billing/catalog-format.js'
import { QuotaFeatures } from '#components/billing/quota-features.jsx'
import { Button } from '#components/ui/button.jsx'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '#components/ui/card.jsx'

function annualSavings(catalog) {
  const savings = catalog.products.flatMap(product => {
    const monthly = product.prices.find(
      price => price.interval === 'month' && price.intervalCount === 1,
    )
    const annual = product.prices.find(
      price => price.interval === 'year' && price.intervalCount === 1,
    )
    if (!monthly || !annual || monthly.currency !== annual.currency) return []

    return [
      Math.round((1 - annual.unitAmount / (monthly.unitAmount * 12)) * 100),
    ]
  })

  return savings.length && savings.every(value => value === savings[0])
    ? savings[0]
    : null
}

export function BillingPeriodControl({ catalog, onChange, value }) {
  const savings = annualSavings(catalog)

  return (
    <fieldset className='flex rounded-md border bg-muted/50 p-0.5'>
      <legend className='sr-only'>Billing period</legend>
      {[
        ['month', 'Monthly'],
        ['year', 'Annual'],
      ].map(([period, label]) => (
        <label className='cursor-pointer' key={period}>
          <input
            checked={value === period}
            className='peer sr-only'
            name='billing-period'
            onChange={() => onChange(period)}
            type='radio'
            value={period}
          />
          <span
            className={`flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 ${
              value === period
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
            {period === 'year' && savings > 0 && (
              <span className='text-xs font-semibold text-emerald-700'>
                Save {savings}%
              </span>
            )}
          </span>
        </label>
      ))}
    </fieldset>
  )
}

export function PricingCards({
  billingPeriod,
  catalog,
  disabled = false,
  loadingPriceId = null,
  onSelect,
  next,
  trialEligible = true,
}) {
  const checkout = useCheckout({ next })
  const selected = loadingPriceId || checkout.loadingPriceId
  const select =
    onSelect || (priceId => checkout.startCheckout(priceId).catch(() => {}))
  return (
    <>
      {checkout.error && <ErrorAlert>{checkout.error}</ErrorAlert>}
      <div className='grid gap-5 lg:grid-cols-3'>
        {catalog.products.map(product => {
          const price = product.prices.find(
            candidate =>
              candidate.interval === billingPeriod &&
              candidate.intervalCount === 1,
          )
          return (
            <Card className='h-full' key={product.id}>
              <CardHeader>
                <CardTitle>{product.name}</CardTitle>
                {product.description && (
                  <CardDescription>{product.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className='flex flex-1 flex-col gap-6'>
                {price ? (
                  <div className='border-b pb-6'>
                    {price.interval === 'year' ? (
                      <>
                        <p className='text-3xl font-semibold'>
                          {formatCatalogMoney(
                            Math.round(price.unitAmount / 12),
                            price.currency,
                          )}
                          <span className='ml-1 text-sm font-normal text-muted-foreground'>
                            / month
                          </span>
                        </p>
                        <p className='mt-2 text-sm text-muted-foreground'>
                          {formatCatalogMoney(price.unitAmount, price.currency)}{' '}
                          billed annually
                        </p>
                      </>
                    ) : (
                      <>
                        <p className='text-3xl font-semibold'>
                          {formatCatalogMoney(price.unitAmount, price.currency)}
                          <span className='ml-1 text-sm font-normal text-muted-foreground'>
                            / month
                          </span>
                        </p>
                        <p className='mt-1 text-sm text-muted-foreground'>
                          Billed monthly
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  <p className='rounded-lg border p-4 text-sm text-muted-foreground'>
                    Not available for this billing period.
                  </p>
                )}
                <QuotaFeatures features={product.quotaFeatures} />
                <Button
                  className='mt-auto w-full'
                  disabled={disabled || Boolean(selected) || !price}
                  loading={selected === price?.id}
                  onClick={() => select(price.id)}
                  type='button'
                >
                  {trialEligible
                    ? 'Start your 14-day free trial'
                    : 'Continue to checkout'}
                  <ArrowRight aria-hidden='true' />
                </Button>
              </CardContent>
            </Card>
          )
        })}
        <Card className='h-full'>
          <CardHeader>
            <CardTitle>Enterprise</CardTitle>
            <CardDescription>
              Custom requirements, scale, and support for larger organizations.
            </CardDescription>
          </CardHeader>
          <CardContent className='flex flex-1 flex-col gap-6'>
            <p className='text-sm text-muted-foreground'>
              Contact us and we will work with you to match Mono to your
              organization’s use case.
            </p>
            <Button asChild className='mt-auto w-full' variant='outline'>
              <Link href='/enterprise'>Contact us</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
